import Dashboard from 'components/Dashboard'
import { BodyLong, Heading, Table, Link } from '@navikt/ds-react'
import NavHighContrast, { VerticalSeparatorDiv } from '@navikt/hoykontrast'
import React, { useEffect, useState } from 'react'
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter'
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx'
import dark from 'react-syntax-highlighter/dist/esm/styles/prism/atom-dark'
import light from 'react-syntax-highlighter/dist/esm/styles/prism/prism'
import styled, { createGlobalStyle } from 'styled-components'
import '@navikt/ds-css'
import { SettingsFilled } from '@navikt/ds-icons'

SyntaxHighlighter.registerLanguage('jsx', jsx)

const PaddedDiv = styled.div`
  padding:3rem;
`
const HeaderDiv = styled.div`
 display: flex;
 flex-direction: row;
 justify-content: flex-end;
`
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    height: 100%;
    color: var(--navds-color-text-primary);
    background-color: var(--navds-semantic-color-canvas-background);
    line-height: 1.42857143;
    font-family: 'Source Sans Pro', Arial, sans-serif;
  }
`
const MarginUL = styled.ul`
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
`

const Page: React.FC<any> = ({ highContrast }: any): JSX.Element => {
  const [_highContrast, _setHighContrast] = useState<boolean>(highContrast)

  useEffect(() => {
    if (!(highContrast === undefined || highContrast === null)) {
      _setHighContrast(highContrast)
    }
  }, [highContrast])

  return (
    <NavHighContrast highContrast={_highContrast}>
      <GlobalStyle />
      <PaddedDiv>
        {(highContrast === undefined || highContrast === null) && (
          <HeaderDiv>
            <Link
              id='highcontrast-link-id'
              href='#highContrast'
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                _setHighContrast(!_highContrast)
              }}
            >
              high contrast
            </Link>
          </HeaderDiv>
        )}
        <VerticalSeparatorDiv size='2' />
        <Heading size='medium'>
          Dashboard application
        </Heading>
        <VerticalSeparatorDiv size='2' />
        <BodyLong>
          The Dashboard application is a grid-based layout panel where you can place your <strong>widgets</strong>,
          in a similiar way as one can organize window applications in a desktop.
        </BodyLong>
        <VerticalSeparatorDiv size='2' />
        <MarginUL>
          <li>Responsive, with different layouts for phone, tablet and desktop sizes (sm, md and lg)</li>
          <li>Saves your custom layouts into your browser's local storage.</li>
          <li>Add, remove, resize and move any widgets.</li>
          <li>Have several dashboards running at the same time. </li>
          <li>Add your own applications as custom widgets.</li>
        </MarginUL>
        <VerticalSeparatorDiv size='2' />

        <Heading size='small'>
          Simple dashboard
        </Heading>
        <VerticalSeparatorDiv size='2' />
        <BodyLong>
          The default Dashboard renders a default panel with 3 widgets: a <strong>notes</strong> widget,
          a <strong>smiley</strong> widget and a <strong>cat</strong> midget.
        </BodyLong>
        <VerticalSeparatorDiv size='2' />
        <BodyLong>
          Click on the <SettingsFilled /> icon to enter <strong>edit</strong> mode.
          Here you can resize the widgets, delete them, or add new from a given list of default widgets.
        </BodyLong>
        <VerticalSeparatorDiv size='2' />
        <BodyLong>
          There is a mandatory property, <code>id</code>, which is used to namespace the localStorage
          item when saving custom configurations.
        </BodyLong>
        <VerticalSeparatorDiv size='2' />
        <Dashboard
          highContrast={_highContrast}
          id='eessi-pensjon-ui-demo-1'
        />
        <VerticalSeparatorDiv size='2' />
        <SyntaxHighlighter language='javascript' style={_highContrast ? dark : light}>
          {'<Dashboard id=\'eessi-pensjon-ui-demo-1\'/>'}
        </SyntaxHighlighter>
        <VerticalSeparatorDiv size='2' />

        <Heading size='small'>
          Custom dashboard
        </Heading>
        <VerticalSeparatorDiv size='2' />
        <BodyLong>
          This custom dashboard illustrates how the dashboard layout object looks like:
        </BodyLong>
        <VerticalSeparatorDiv size='2' />
        <MarginUL>
          <li>Object keys are tab labels; if only key is used, the tabs are not shown</li>
          <li>There are 3 default breakpoints: <code>sm</code>, <code>md</code> and <code>lg</code>
            (they can be changed in the Dashboard configuration object)
          </li>
          <li>Each breakpoint has a list of widgets, which contain:
            <ul>
              <li><code>i</code>, the widget ID (matched in the widget configuration object)</li>
              <li><code>x</code>, the horizontal position</li>
              <li><code>y</code>, the vertical position</li>
              <li><code>w</code>, the widget width</li>
              <li><code>h</code>, the widget height (conditioned to widget resize)</li>
              <li><code>minW</code>, the minimum widget width</li>
              <li><code>maxW</code>, the maximum widget width</li>
              <li><code>minH</code>, the minimum widget height</li>
              <li><code>maxH</code>, the maximum widget height</li>
            </ul>
          </li>
        </MarginUL>
        <VerticalSeparatorDiv size='2' />
        <Dashboard
          id='eessi-pensjon-ui-demo-2'
          highContrast={_highContrast}
          defaultLayouts={[{
            label: 'Side 1',
            key: 'side1',
            body: {
              lg: [
                { i: 'w-1-note', x: 0, y: 0, w: 4, h: 5, minW: 4, maxW: 6, minH: 5, maxH: 999 }
              ],
              md: [
                { i: 'w-1-note', x: 0, y: 0, w: 2, h: 6, minW: 2, maxW: 3, minH: 1, maxH: 999 }
              ],
              sm: [
                { i: 'w-1-note', x: 0, y: 0, w: 1, h: 1, minW: 1, maxW: 1, minH: 1, maxH: 999 }
              ]
            }
          }, {
            label: 'Side 2',
            key: 'side2',
            body: {
              lg: [
                { i: 'w-2-smiley', x: 0, y: 0, w: 4, h: 5, minW: 4, maxW: 6, minH: 5, maxH: 999 }
              ],
              md: [
                { i: 'w-2-smiley', x: 0, y: 0, w: 2, h: 6, minW: 2, maxW: 3, minH: 1, maxH: 999 }
              ],
              sm: [
                { i: 'w-2-smiley', x: 0, y: 0, w: 1, h: 1, minW: 1, maxW: 1, minH: 1, maxH: 999 }
              ]
            }
          }, {
            label: 'Side 3',
            key: 'side3',
            body: {
              lg: [
                { i: 'w-3-cat', x: 0, y: 0, w: 4, h: 5, minW: 4, maxW: 6, minH: 5, maxH: 999 }
              ],
              md: [
                { i: 'w-3-cat', x: 0, y: 0, w: 2, h: 6, minW: 2, maxW: 3, minH: 1, maxH: 999 }
              ],
              sm: [
                { i: 'w-3-cat', x: 0, y: 0, w: 1, h: 1, minW: 1, maxW: 1, minH: 1, maxH: 999 }
              ]
            }
          }]}
          defaultWidgets={[{
            i: 'w-1-note',
            type: 'note',
            title: 'Note widget',
            visible: true,
            options: {
              backgroundColor: 'yellow',
              content: '<ul><li>Eggs</li><li>Milk</li><li>Bread</li></ul>'
            }
          }, {
            i: 'w-2-smiley',
            type: 'smiley',
            title: 'Smiley widget',
            visible: true,
            options: {
              mood: '😁'
            }
          }, {
            i: 'w-3-cat',
            type: 'cat',
            title: 'Cat midget',
            visible: true,
            options: {}
          }]}
          defaultConfig={{
            cols: { lg: 12, md: 3, sm: 1 },
            breakpoints: { lg: 900, md: 600, sm: 0 },
            margin: [10, 10],
            containerPadding: [10, 10],
            rowHeight: 30,
            defaultTab: 'default',
            version: 1
          }}
          defaultTab='side1'
          allowedWidgets={['cat', 'smiley', 'note']}
        />
        <VerticalSeparatorDiv size='2' />

        <SyntaxHighlighter language='javascript' style={_highContrast ? dark : light}>
          {'<Dashboard\n' +
          '  id=\'eessi-pensjon-ui-demo\'\n' +
          '  defaultLayout={{\n' +
          '  \'Side 1\': {\n' +
          '    lg: [\n' +
          '      { i: \'w-1-note\', x: 0, y: 0, w: 4, h: 5, minW: 4, maxW: 6, minH: 5, maxH: 999 }\n' +
          '    ],\n' +
          '    md: [\n' +
          '      { i: \'w-1-note\', x: 0, y: 0, w: 2, h: 6, minW: 2, maxW: 3, minH: 1, maxH: 999 }\n' +
          '    ],\n' +
          '    sm: [\n' +
          '      { i: \'w-1-note\', x: 0, y: 0, w: 1, h: 1, minW: 1, maxW: 1, minH: 1, maxH: 999 }\n' +
          '    ]\n' +
          '  },\n' +
          '  \'Side 2\': {\n' +
          '    lg: [\n' +
          '      { i: \'w-2-smiley\', x: 0, y: 0, w: 4, h: 5, minW: 4, maxW: 6, minH: 5, maxH: 999 }\n' +
          '    ],\n' +
          '    md: [\n' +
          '      { i: \'w-2-smiley\', x: 0, y: 0, w: 2, h: 6, minW: 2, maxW: 3, minH: 1, maxH: 999 }\n' +
          '    ],\n' +
          '    sm: [\n' +
          '      { i: \'w-2-smiley\', x: 0, y: 0, w: 1, h: 1, minW: 1, maxW: 1, minH: 1, maxH: 999 }\n' +
          '    ]\n' +
          '  },\n' +
          '  \'Side 3\': {\n' +
          '    lg: [\n' +
          '      { i: \'w-3-cat\', x: 0, y: 0, w: 4, h: 5, minW: 4, maxW: 6, minH: 5, maxH: 999 }\n' +
          '    ],\n' +
          '    md: [\n' +
          '      { i: \'w-3-cat\', x: 0, y: 0, w: 2, h: 6, minW: 2, maxW: 3, minH: 1, maxH: 999 }\n' +
          '    ],\n' +
          '    sm: [\n' +
          '      { i: \'w-3-cat\', x: 0, y: 0, w: 1, h: 1, minW: 1, maxW: 1, minH: 1, maxH: 999 }\n' +
          '    ]\n' +
          '  }}\n' +
          '  defaultWidgets={[{\n' +
          '    i: \'w-1-note\',\n' +
          '    type: \'note\',\n' +
          '    title: \'Note widget\',\n' +
          '    visible: true,\n' +
          '    options: {\n' +
          '      backgroundColor: \'yellow\',\n' +
          '      content: \'<ul><li>Eggs</li><li>Milk</li><li>Bread</li></ul>\'\n' +
          '    }\n' +
          '  }, {\n' +
          '    i: \'w-2-smiley\',\n' +
          '    type: \'smiley\',\n' +
          '    title: \'Smiley widget\',\n' +
          '    visible: true,\n' +
          '    options: {\n' +
          '      mood: \'😁\'\n' +
          '    }\n' +
          '  }, {\n' +
          '    i: \'w-3-cat\',\n' +
          '    type: \'cat\',\n' +
          '    title: \'Cat midget\',\n' +
          '    visible: true,\n' +
          '    options: {}\n' +
          '  }]}' +
          '  defaultConfig={{\n' +
          '    cols: { lg: 12, md: 3, sm: 1 },\n' +
          '    breakpoints: { lg: 900, md: 600, sm: 0 },\n' +
          '    margin: [10, 10],\n' +
          '    containerPadding: [10, 10],\n' +
          '    rowHeight: 30,\n' +
          '    defaultTab: \'default\',\n' +
          '    version: 1\n' +
          '  }}' +
          '  defaultTab=\'default\'\n' +
          '  allowedWidgets={[\'cat\', \'smiley\', \'note\']}\n' +
          '/>'}
        </SyntaxHighlighter>
        <VerticalSeparatorDiv size='2' />

        <Heading size='small'>
          Component import
        </Heading>
        <VerticalSeparatorDiv size='2' />
        <SyntaxHighlighter language='javascript' style={_highContrast ? dark : light}>
          {'import Dashboard from \'nav-dashboard\''}
        </SyntaxHighlighter>

        <VerticalSeparatorDiv size='2' />
        <Heading size='small'>
          React props
        </Heading>
        <VerticalSeparatorDiv size='2' />
        <table className='tabell'>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Property</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Required</Table.HeaderCell>
              <Table.HeaderCell>Description</Table.HeaderCell>
              <Table.HeaderCell>Default</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.DataCell>afterLayoutChange</Table.DataCell>
              <Table.DataCell><code>function</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>Callback function triggered everytime the dashboard layout changes.</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>allowedWidgets</Table.DataCell>
              <Table.DataCell><code>list</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>List of allowed Dashboard widgets. You will be able to only add those widgets to the dashboard, and you will only be able to see these widget types. By default, you are allowed to see/add all types of widgets.</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>className</Table.DataCell>
              <Table.DataCell><code>string</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>className prop</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>configurable</Table.DataCell>
              <Table.DataCell><code>boolean</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>Boolean toggle if we want a configurable dashboard, that is, to see dashboard control panel</Table.DataCell>
              <Table.DataCell>true</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>defaultConfig</Table.DataCell>
              <Table.DataCell><code>list</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>Dashboard configuration object that overrides the default configuration. Useful for margin/padding/styling/breakpoint set</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>defaultLayout</Table.DataCell>
              <Table.DataCell><code>object</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>Dashboard layout object that overrides the default layout.</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>defaultTaIndex</Table.DataCell>
              <Table.DataCell><code>number</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>Active dashboard tab index, if there is more than one dashboard in the dashboard layout.</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>defaultWidgets</Table.DataCell>
              <Table.DataCell><code>list</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>Dashboard widgets configuration that overrides the default widgets.</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>extraWidgets</Table.DataCell>
              <Table.DataCell><code>object</code></Table.DataCell>
              <Table.DataCell>false</Table.DataCell>
              <Table.DataCell>Append your custom Widgets to the dashboard</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>id</Table.DataCell>
              <Table.DataCell><code>string</code></Table.DataCell>
              <Table.DataCell>true</Table.DataCell>
              <Table.DataCell>Dashboard ID used to namespace the localStorage items for future use</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
            <Table.Row>
              <Table.DataCell>labels</Table.DataCell>
              <Table.DataCell><code>object</code></Table.DataCell>
              <Table.DataCell>true</Table.DataCell>
              <Table.DataCell>Optional label object used to override default labels.</Table.DataCell>
              <Table.DataCell>-</Table.DataCell>
            </Table.Row>
          </Table.Body>
        </table>
      </PaddedDiv>
    </NavHighContrast>
  )
}

export default Page
